<template>
  <div
    v-if="errorCode === 503"
    class="the-recipe-amp-error"
  >
    <AmpError />
  </div>

  <div
    v-else-if="recipe"
    class="the-recipe-amp"
  >
    <component
      :is="'amp-geo'"
      layout="nodisplay"
    />

    <main class="the-recipe-amp__main">
      <RecipeCardAmp
        :id="recipe.id"
        :author="recipe.author"
        :cooking-time="recipe.totalTime"
        :cover="cover"
        :expert="recipe.expert"
        :title="recipe.title"
        class="the-recipe-amp__card"
      />
      <section class="the-recipe-amp__article">
        <section
          v-if="recipe.excerpt"
          class="the-recipe-amp__description"
        >
          <TheContent
            v-for="item of sanitizedDescription"
            :key="item.id"
            :elem="item"
          />
        </section>

        <PrintButton
          :link="toPrintUrl"
          class="the-recipe-amp__print-button"
        />

        <div class="the-recipe-amp__content-container">
          <template
            v-for="(item, idx) in recipe.parsedContent"
            :key="item.id"
          >
            <TheContent :elem="item" />

            <!-- Реклама внутри рецепта -->
            <AdsAmp
              v-if="idx === 1"
              class="the-recipe-amp__ad the-recipe-amp__ad_large-margin"
            />
          </template>
        </div>
      </section>

      <div class="the-recipe-amp__categories">
        <div class="the-recipe-amp__categories-wrapper">
          <RecipeCategoryLink
            v-for="(item, index) of recipe.breadcrumb"
            :key="item.title + index"
            :link="item.url"
            :text="item.title + (item.own === 'kitchen' ? ' кухня' : '')"
          />
        </div>
      </div>

      <ArticleNote
        :notes="notes"
        class="the-recipe-amp__notes"
      />

      <AmpMessage
        :link="recipeUrl"
        class="the-recipe-amp__message"
      />

      <ShareBlock
        class="the-recipe-amp__share-block"
        type="recipe"
      />
    </main>

    <!-- Похожие рецепты -->
    <RecommendedPostsContainer
      :id="recipe.id"
      :recipes="recipe.similarPosts?.slice(0, 4)"
      :show-title="false"
      class="the-recipe-amp__recommended-recipes similar"
      next-page-hide
    />

    <!-- Реклама под рецептом -->
    <AdsAmp class="the-recipe-amp__ad the-recipe-amp__ad_large-margin" />

    <CommentsContainerAmp
      :id="recipe.id"
      :article-url="recipeUrl"
      class="the-recipe-amp__comments"
    />

    <!--Рекомендованные посты для рецептов, -->
    <!--выводится специфическая категория, определяется на бэке -->
    <!--.recomended используется для собыйтий в амп, не изменять без правок в yaAmpEvets.ts -->
    <RecommendedPostsContainer
      :id="recipe.id"
      class="the-recipe-amp__recommended-recipes recommended"
      next-page-hide
    />

    <RecipeAnalytics />

    <FullScreenGallery
      v-if="gallery.exists"
      :id="gallery.id"
      :images="gallery.images.value"
    />

    <AmpActionSheet
      v-for="(refContents, index) in ampRefContent"
      :id="getRefId(refContents.parentId)"
      :key="refContents.parentId + index"
      :ref-content="refContents.content"
    />
  </div>
</template>

<script lang="ts">
  import { getImagesBySizes } from '@devhacker/shared/utils/image';
  import CommentsContainerAmp from '~/components/Amp/Comments/CommentsContainer.amp.vue';
  import RecommendedPostsContainer from '~/containers/RecommendedPostsContainer.amp/RecommendedPostsContainer.amp';
  import RecipeCardAmp from '~/components/RecipeCard.amp.vue';
  import AmpMessage from '~/components/AmpMessage.amp.vue';
  import ShareBlock from '~/components/ShareBlock.amp/ShareBlock.amp.vue';
  import FullScreenGallery from '~/components/FullScreenGallery.amp/FullScreenGallery.amp.vue';
  import AmpActionSheet from '~/components/AmpActionSheet.amp.vue';
  import ArticleNote, { getArticleNoteProps } from '~/components/ArticleNote.amp.vue';
  import RecipeCategoryLink from '~/components/RecipeCategoryLink.amp.vue';
  import RecipeAnalytics from '~/components/Analytics/RecipeAnalytics.amp.vue';
  import PrintButton from '~/components/old-recipes/PrintButton/PrintButton.amp.vue';
  import TheContent from '~/components/content/TheContent';
  import AmpError from '~/components/AmpError.amp.vue';
  import AdsAmp from '@devhacker/shared/components/ads/AdsAmp.vue';

  import { ARTICLE_IMAGE_SIZES } from '~/constants/imageSizes';
  import type { TArticleNoteItem } from '~/types';
  import { trailingSlash } from '~/utils';
  import { getQueryValue } from '~/utils/router';
  import { useAmpCustomElement } from '@devhacker/shared/composables/useAmpCustomElement';
  import type { FetchRecipeResponse } from '~/types/api/recipes';
  import { useAmpContent } from '~/composables/useAmpContent';
  import { getRefId } from '~/utils/content/Amp/getRefId';
  import { sanitizeAmp } from '~/libs/sanitize-amp';
  import { prepareContent } from '~/utils/content/prepareContent';
  import { RecipeSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
  import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';
  import { useRecipeAnalyticsHead } from '~/domains/head/analytics/services/useRecipeAnalyticsHead';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';
  import type { FullRecipe } from '~/types/typings';

  export default defineNuxtComponent({
    name: 'TheRecipeAmp',

    amp: 'only',

    components: {
      RecipeCardAmp,
      CommentsContainerAmp,
      RecommendedPostsContainer,
      AmpMessage,
      ShareBlock,
      ArticleNote,
      RecipeCategoryLink,
      RecipeAnalytics,
      PrintButton,
      TheContent,
      AmpError,
      FullScreenGallery,
      AmpActionSheet,
      AdsAmp,
    },

    async setup() {
      definePageMeta({
        layout: 'default-amp',
      });

      useAmpCustomElement('amp-geo');

      const errorCode = ref<number | null>(null);
      const nuxtApp = useNuxtApp();
      const route = useRoute();
      const slug = getQueryValue(route.params.slug);
      const recipe = ref<FullRecipe>();
      const recipeMeta = ref<FetchRecipeResponse['meta']>();
      const state = ref();
      const recipeUrl = ref('');
      const toPrintUrl = computed(() => recipeUrl.value + '#toPrint');

      const {
        data: response,
        status,
        error,
      } = await useAsyncData(() => nuxtApp.$recipesApi.fetchRecipe(slug));

      if (error.value || !response.value?.item) {
        // this.$nuxt.context.res.statusCode = errorCode;
        // this.$sentry.captureException(error);
        throw createError(error.value);
      }

      state.value = status;
      recipeMeta.value = response.value?.meta;

      const { gallery, ampRefContent, processedAmpContent } = useAmpContent(
        response.value?.item.parsedContent ?? [],
      );

      // Typescript doesn't see check in error block
      if (response.value?.item) {
        recipe.value = {
          ...response.value?.item,
          parsedContent: processedAmpContent.value,
        };
      }

      const seo = parseSeoOrThrow(RecipeSeoInputSchema, response.value?.seo);
      recipeUrl.value = trailingSlash(seo.canonical);

      useSeoHead(seo);
      useRecipeAnalyticsHead(seo);
      useHead({
        bodyAttrs: {
          class: 'amp-geo-pending',
        },
      });

      const sanitizedDescription = computed(() => {
        return sanitizeAmp(prepareContent(recipe?.value?.excerpt, true));
      });

      return {
        recipe,
        recipeMeta,
        errorCode,
        state,
        recipeUrl,
        toPrintUrl,
        gallery,
        ampRefContent,
        sanitizedDescription,
      };
    },

    computed: {
      cover(): {
        normal: string;
        retina: string;
      } {
        if (!this.recipe) {
          return {
            normal: '',
            retina: '',
          };
        }
        const covers = getImagesBySizes(
          [ARTICLE_IMAGE_SIZES.L.width, ARTICLE_IMAGE_SIZES.L.width * 2],
          this.recipe.image.sizes,
        );

        return {
          normal: covers[0]?.url ?? '',
          retina: covers[1]?.url ?? '',
        };
      },

      /**
       * Данные для отображения под статьей.
       *
       * @return Пропсы для компонента ArticleNote
       */
      notes(): TArticleNoteItem[] {
        if (!this.recipeMeta) {
          return [];
        }

        const { source, coverSource, customAuthors } = this.recipeMeta;

        return getArticleNoteProps(source, coverSource, customAuthors, 'Над рецептом работали: ');
      },
    },

    methods: {
      getRefId,
    },
  });
</script>

<style lang="scss">
  :root {
    --main-color: #{$main};
    --preview-header-color: #a0270f;
    --bg-color-substrate: #fbf8f8;
  }
</style>

<style lang="scss" scoped>
  .the-recipe-amp {
    max-width: 640px;
    margin: 0 auto 16px;

    &__content-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin: 0px 16px;
    }

    &-error {
      max-width: 640px;
      margin: 0 auto;
    }

    &__comments {
      margin-bottom: 16px;
    }
    &__recommended-recipes {
      margin: 16px 0px;
    }
    &__main {
      background: white;
    }
    &__ad {
      display: block;
      @include margin-x(auto);

      &_small-margin {
        @include margin-y(8px);
      }
      &_large-margin {
        @include margin-y(16px);
      }
    }

    &__share-block,
    &__message,
    &__notes,
    &__categories,
    &__description,
    &__list,
    &__cooking-steps,
    &__print-button {
      @include margin-x(16px);
    }

    &__description {
      @include fontNormalText;

      margin-top: 24px;
      margin-bottom: 16px;
    }

    &__list {
      margin-bottom: 24px;
    }

    &__categories {
      margin-bottom: 22px;
    }

    &__notes {
      margin-bottom: 24px;
    }

    &__main {
      padding-bottom: 24px;
      border: 1px solid $borderColor;
      border-top: none;
    }

    &__print-button {
      margin-bottom: 24px;
    }

    &__card {
      width: calc(100% + 2px);
      margin-left: -1px;
    }
    &__categories {
      overflow: hidden;
    }

    &__categories-wrapper {
      margin-left: -8px;
    }
  }
</style>
